import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import './AppDetailScreen.css';

import backIcon from '../../assets/backspace-icon.png';
import api from '../../api';

function formatDate(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return '—';
  }
}

function AppDetailScreen({ user }) {
  const navigate = useNavigate();
  const { id } = useParams();

  const [appData, setAppData] = useState(null);
  const [domainRegistry, setDomainRegistry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState('');
  const [error, setError] = useState('');
  const [note, setNote] = useState('');

  const fetchAppDetail = async () => {
    try {
      setLoading(true);
      setError('');

      const { data } = await api.get(`/admin/apps/${id}`);
      setAppData(data?.appProfile || null);
      setDomainRegistry(data?.domainRegistry || null);
      setNote(data?.appProfile?.reviewNote || '');
    } catch (err) {
      console.error('Failed to fetch app detail:', err);
      setError(err?.response?.data?.message || 'Failed to load app details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAppDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleApprove = async () => {
    try {
      setActionLoading('approve');
      setError('');

      await api.patch(`/admin/apps/${id}/approve`, {
        note: note.trim(),
      });

      await fetchAppDetail();
    } catch (err) {
      console.error('Approve failed:', err);
      setError(err?.response?.data?.message || 'Failed to approve app');
    } finally {
      setActionLoading('');
    }
  };

  const handleReject = async () => {
    try {
      setActionLoading('reject');
      setError('');

      await api.patch(`/admin/apps/${id}/reject`, {
        note: note.trim(),
      });

      await fetchAppDetail();
    } catch (err) {
      console.error('Reject failed:', err);
      setError(err?.response?.data?.message || 'Failed to reject app');
    } finally {
      setActionLoading('');
    }
  };

  if (!user || user.userType !== 'admin') {
    return <div className="admin-app-detail-denied">Access denied.</div>;
  }

  if (loading) {
    return <div className="admin-app-detail-state">Loading app details...</div>;
  }

  if (error && !appData) {
    return <div className="admin-app-detail-state admin-app-detail-error">{error}</div>;
  }

  if (!appData) {
    return <div className="admin-app-detail-state">App not found.</div>;
  }

  const owner = appData.primaryOwner || {};
  const linkedUser = appData.linkedUser || {};

  return (
    <div className="admin-app-detail-container">
      <div className="admin-app-detail-topbar">
        <img
          src={backIcon}
          alt="Back"
          className="admin-app-detail-back-icon"
          onClick={() => navigate('/admin/apps')}
        />
        <h2>App Review Detail</h2>
      </div>

      <div className="admin-app-detail-content">
        {error ? <div className="admin-app-detail-banner-error">{error}</div> : null}

        <div className="admin-app-detail-card">
          <div className="admin-app-detail-header">
            <img
              src={appData.profilePic || '/profile/placeholder-logo.png'}
              alt={appData.name}
              className="admin-app-detail-avatar"
            />

            <div className="admin-app-detail-header-main">
              <h3>{appData.name}</h3>
              <div className="admin-app-detail-username">@{appData.username}</div>
              <div className={`admin-app-detail-pill ${appData.reviewStatus || 'pending'}`}>
                {appData.reviewStatus || 'pending'}
              </div>
            </div>
          </div>

          <div className="admin-app-detail-grid">
            <div><strong>Category:</strong> {appData.category || '—'}</div>
            <div><strong>Status:</strong> {appData.status || '—'}</div>
            <div><strong>Verification:</strong> {appData.domainVerificationStatus || '—'}</div>
            <div><strong>Domain:</strong> {appData.domain || '—'}</div>
            <div><strong>Released Domain:</strong> {appData.releasedDomain || '—'}</div>
            <div><strong>App URL:</strong> {appData.appUrl || '—'}</div>
            <div><strong>Created:</strong> {formatDate(appData.createdAt)}</div>
            <div><strong>Updated:</strong> {formatDate(appData.updatedAt)}</div>
            <div><strong>Requested:</strong> {formatDate(appData.reviewRequestedAt)}</div>
            <div><strong>Reviewed:</strong> {formatDate(appData.reviewedAt)}</div>
            <div><strong>Verified At:</strong> {formatDate(appData.domainVerifiedAt)}</div>
            <div><strong>Wallet Enabled:</strong> {appData.walletEnabled ? 'Yes' : 'No'}</div>
          </div>

          <div className="admin-app-detail-section">
            <h4>Bio</h4>
            <p>{appData.bio || 'No bio provided.'}</p>
          </div>
        </div>

        <div className="admin-app-detail-card">
          <h4>Owner Info</h4>
          <div className="admin-app-detail-grid">
            <div><strong>Name:</strong> {owner.name || '—'}</div>
            <div><strong>Username:</strong> {owner.username ? `@${owner.username}` : '—'}</div>
            <div><strong>Email:</strong> {owner.email || '—'}</div>
            <div><strong>User Type:</strong> {owner.userType || '—'}</div>
          </div>
        </div>

        <div className="admin-app-detail-card">
          <h4>Linked App User</h4>
          <div className="admin-app-detail-grid">
            <div><strong>Name:</strong> {linkedUser.name || '—'}</div>
            <div><strong>Username:</strong> {linkedUser.username ? `@${linkedUser.username}` : '—'}</div>
            <div><strong>Email:</strong> {linkedUser.email || '—'}</div>
            <div><strong>Account Type:</strong> {linkedUser.accountType || '—'}</div>
          </div>
        </div>

        <div className="admin-app-detail-card">
          <h4>Domain Registry</h4>
          {domainRegistry ? (
            <div className="admin-app-detail-grid">
              <div><strong>Times Used:</strong> {domainRegistry.timesUsed ?? '—'}</div>
              <div><strong>Was Ever Verified:</strong> {domainRegistry.wasEverVerified ? 'Yes' : 'No'}</div>
              <div><strong>Was Ever Active:</strong> {domainRegistry.wasEverActive ? 'Yes' : 'No'}</div>
              <div><strong>Requires Review:</strong> {domainRegistry.requiresAdminReview ? 'Yes' : 'No'}</div>
              <div><strong>Last Review Status:</strong> {domainRegistry.lastReviewStatus || '—'}</div>
              <div><strong>Last Released At:</strong> {formatDate(domainRegistry.lastReleasedAt)}</div>
            </div>
          ) : (
            <p>No domain registry record found.</p>
          )}
        </div>

        <div className="admin-app-detail-card">
          <h4>Review Note</h4>
          <textarea
            className="admin-app-detail-note"
            placeholder="Add admin note..."
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />

          <div className="admin-app-detail-actions">
            <button
              className="admin-app-detail-btn reject"
              onClick={handleReject}
              disabled={actionLoading === 'approve' || actionLoading === 'reject'}
            >
              {actionLoading === 'reject' ? 'Rejecting...' : 'Reject'}
            </button>

            <button
              className="admin-app-detail-btn approve"
              onClick={handleApprove}
              disabled={actionLoading === 'approve' || actionLoading === 'reject'}
            >
              {actionLoading === 'approve' ? 'Approving...' : 'Approve'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AppDetailScreen;
